import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../contexts/ToastContext';
import { getAdminWorkshop, deleteWorkshop } from '../../api/client';
import Button from '../../components/ui/Button';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  Clock,
  Calendar,
  Users,
  IndianRupee,
  Link as LinkIcon,
  Loader2,
  Copy,
  CheckCircle,
  User,
  Mail,
} from 'lucide-react';

interface Workshop {
  id: number;
  title: string;
  slug: string;
  description: string;
  duration: string;
  start_date: string;
  end_date: string;
  price: number;
  max_seats: number;
  seats_filled: number;
  status: string;
  meeting_link: string;
  trainer_id: number;
  trainer_name: string;
  trainer_email: string;
  trainer_avatar: string;
  created_at: string;
}

export default function AdminWorkshopDetail(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [workshop, setWorkshop] = useState<Workshop | null>(null);
  const [enrollmentCount, setEnrollmentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    fetchWorkshop();
  }, [id]);

  const fetchWorkshop = async () => {
    setLoading(true);
    try {
      const res = await getAdminWorkshop(Number(id));
      setWorkshop(res.data?.workshop || null);
      setEnrollmentCount(res.data?.enrollment_count || 0);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load workshop', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!workshop || !confirm('Delete this workshop?')) return;
    setDeleting(true);
    try {
      await deleteWorkshop(workshop.id);
      toast.show('Workshop deleted', 'success');
      navigate('/admin/workshops');
    } catch (err: any) {
      toast.show(err.message || 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyLink = () => {
    if (!workshop?.meeting_link) return;
    navigator.clipboard.writeText(workshop.meeting_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'live': return 'bg-green-50 text-green-700 border-green-200';
      case 'upcoming': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'completed': return 'bg-slate-100 text-slate-600 border-slate-200';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </AdminLayout>
    );
  }

  if (!workshop) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <p className="text-slate-500">Workshop not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/workshops')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Workshops
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const seatsRemaining = workshop.max_seats - (workshop.seats_filled || 0);
  const revenue = (workshop.price || 0) * (workshop.seats_filled || 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/workshops')}
              className="p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{workshop.title}</h1>
              <p className="text-sm text-slate-500">{workshop.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${statusColor(workshop.status)}`}>
              {workshop.status}
            </span>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Edit3 className="w-4 h-4" />}
              onClick={() => navigate(`/admin/workshops?edit=${workshop.id}`)}
            >
              Edit
            </Button>
            <Button
              variant="danger"
              size="sm"
              leftIcon={deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              onClick={handleDelete}
              isLoading={deleting}
            >
              Delete
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-slate-500 uppercase">Duration</span>
            </div>
            <p className="text-lg font-bold text-slate-900">{workshop.duration || '-'}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-slate-500 uppercase">Price</span>
            </div>
            <p className="text-lg font-bold text-slate-900">₹{workshop.price}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-slate-500 uppercase">Seats</span>
            </div>
            <p className="text-lg font-bold text-slate-900">{workshop.seats_filled || 0} / {workshop.max_seats}</p>
            <p className="text-xs text-slate-500">{seatsRemaining} remaining</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-4 h-4 text-green-500" />
              <span className="text-xs font-semibold text-slate-500 uppercase">Revenue</span>
            </div>
            <p className="text-lg font-bold text-slate-900">₹{revenue}</p>
            <p className="text-xs text-slate-500">{enrollmentCount} enrollments</p>
          </motion.div>
        </div>

        {/* Dates */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-slate-700">Start Date</span>
            </div>
            <p className="text-sm text-slate-900">{formatDate(workshop.start_date)}</p>
          </div>
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-slate-700">End Date</span>
            </div>
            <p className="text-sm text-slate-900">{formatDate(workshop.end_date)}</p>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Description</h3>
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
            {workshop.description || 'No description provided.'}
          </p>
        </div>

        {/* Trainer */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Trainer</h3>
          {workshop.trainer_id ? (
            <div className="flex items-center gap-3">
              {workshop.trainer_avatar ? (
                <img src={workshop.trainer_avatar} alt={workshop.trainer_name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold">
                  {workshop.trainer_name?.charAt(0) || '?'}
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-slate-900">{workshop.trainer_name}</p>
                <p className="text-xs text-slate-500">{workshop.trainer_email}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">No trainer assigned</p>
          )}
        </div>

        {/* Meeting Link */}
        {workshop.meeting_link && (
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Meeting Link</h3>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-50 rounded-xl px-4 py-2.5 text-sm text-slate-600 truncate">
                {workshop.meeting_link}
              </div>
              <button
                onClick={handleCopyLink}
                className="p-2.5 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-colors"
                title="Copy link"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
